"use client";

import { useEffect, useState, useTransition } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import {
  searchAttractions,
  linkArtist,
  createArtist,
  updateEventAttraction,
  type SearchAttractionsResult,
} from "@/app/admin/actions";
import { useModalZIndex } from "@/lib/use-modal-z-index";
import { TimeInput } from "./time-input";
import { Select } from "@/components/select";
import { AppQueryProvider } from "@/app/providers";

interface AddLineupEntryModalProps {
  eventId: number;
  existingStages?: string[];
  onClose: () => void;
}

export function AddLineupEntryModal(props: AddLineupEntryModalProps) {
  return <AppQueryProvider><AddLineupEntryModalContent {...props} /></AppQueryProvider>;
}

function AddLineupEntryModalContent({
  eventId,
  existingStages = [],
  onClose,
}: AddLineupEntryModalProps) {
  const zIndex = useModalZIndex();
  const [isPending, startTransition] = useTransition();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchAttractionsResult[]>(
    []
  );
  const [isSearching, setIsSearching] = useState(false);
  const [selectedArtist, setSelectedArtist] = useState<SearchAttractionsResult | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newArtistName, setNewArtistName] = useState("");
  const [newArtistImageUrl, setNewArtistImageUrl] = useState("");
  const [stage, setStage] = useState("");
  const [customStage, setCustomStage] = useState("");
  const [setTime, setSetTime] = useState("");
  const [setEndTime, setSetEndTime] = useState("");
  const queryClient = useQueryClient();
  const router = useRouter();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // Debounced search
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const data = await searchAttractions(searchQuery);
        setSearchResults(data);
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const linkAndSetDetails = async (attractionId: number) => {
    await linkArtist(eventId, attractionId);
    const effectiveStage = stage === "__custom__" ? customStage.trim() : stage.trim();
    if (effectiveStage || setTime || setEndTime) {
      await updateEventAttraction(eventId, attractionId, {
        stage: effectiveStage || null,
        set_time: setTime || null,
        set_end_time: setEndTime || null,
      });
    }
    queryClient.invalidateQueries({ queryKey: ["admin", "events"] });
    router.refresh();
    onClose();
  };

  const handleSelectArtist = (artist: SearchAttractionsResult) => {
    setSelectedArtist(artist);
    setShowCreateForm(false);
    setSearchQuery("");
    setSearchResults([]);
  };

  const handleAdd = () => {
    if (selectedArtist) {
      startTransition(() => linkAndSetDetails(selectedArtist.id));
    } else if (showCreateForm && newArtistName.trim()) {
      startTransition(async () => {
        const newId = await createArtist(
          newArtistName.trim(),
          [],
          null,
          newArtistImageUrl.trim() || null
        );
        await linkAndSetDetails(newId);
      });
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/70 p-4"
      style={{ zIndex }}
      onClick={onClose}
    >
      <div
        className="relative flex w-full max-w-sm flex-col overflow-visible border border-neutral-800 bg-surface"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Corner markers */}
        <div className="absolute -left-1 -top-1 h-3 w-3 border-l border-t border-acid" />
        <div className="absolute -right-1 -top-1 h-3 w-3 border-r border-t border-acid" />
        <div className="absolute -bottom-1 -left-1 h-3 w-3 border-b border-l border-acid" />
        <div className="absolute -bottom-1 -right-1 h-3 w-3 border-b border-r border-acid" />

        {/* Header */}
        <div className="flex items-center justify-between gap-4 border-b border-neutral-800 p-5">
          <h2 className="font-mono text-sm font-bold tracking-wider text-white">
            ADD TO LINEUP
          </h2>
          <button
            onClick={onClose}
            className="shrink-0 text-neutral-500 transition-colors hover:text-white"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M4 4L12 12M12 4L4 12"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          {/* Artist selection */}
          {selectedArtist ? (
            <div className="flex items-center gap-2 rounded border border-neutral-700 bg-dark p-2">
              <div className={`h-1.5 w-1.5 shrink-0 rounded-full ${selectedArtist.spotify_id ? "bg-green-500" : "bg-neutral-600"}`} />
              <span className="flex-1 truncate font-mono text-[11px] text-neutral-300">{selectedArtist.name}</span>
              <button
                onClick={() => setSelectedArtist(null)}
                className="shrink-0 font-mono text-[10px] text-neutral-500 hover:text-neutral-300"
              >
                &times;
              </button>
            </div>
          ) : showCreateForm ? (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="font-mono text-[10px] text-neutral-500">NEW ARTIST</label>
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="font-mono text-[10px] text-neutral-500 hover:text-neutral-300"
                >
                  &times; back to search
                </button>
              </div>
              <div className="space-y-2 rounded border border-neutral-700 bg-dark p-2">
                <input
                  type="text"
                  value={newArtistName}
                  onChange={(e) => setNewArtistName(e.target.value)}
                  placeholder="Artist name"
                  autoFocus
                  className="w-full rounded border border-neutral-700 bg-surface px-2 py-1 font-mono text-[11px] text-neutral-300 placeholder-neutral-600 outline-none focus:border-acid/50"
                />
                <input
                  type="text"
                  value={newArtistImageUrl}
                  onChange={(e) => setNewArtistImageUrl(e.target.value)}
                  placeholder="Image URL (optional)"
                  className="w-full rounded border border-neutral-700 bg-surface px-2 py-1 font-mono text-[11px] text-neutral-300 placeholder-neutral-600 outline-none focus:border-acid/50"
                />
              </div>
            </div>
          ) : (
            <div>
              <label className="mb-1 block font-mono text-[10px] text-neutral-500">
                SEARCH ARTIST
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Type to search..."
                autoFocus
                className="w-full rounded border border-neutral-700 bg-dark px-2 py-1.5 font-mono text-[11px] text-neutral-300 placeholder-neutral-600 outline-none focus:border-acid/50"
              />
              {isSearching && (
                <p className="mt-1 font-mono text-[10px] text-neutral-600">
                  searching...
                </p>
              )}
              {searchResults.length > 0 && (
                <div className="mt-1 max-h-32 overflow-y-auto rounded border border-neutral-700 bg-dark">
                  {searchResults.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => handleSelectArtist(r)}
                      disabled={isPending}
                      className="flex w-full items-center gap-2 px-2 py-1.5 text-left transition-colors hover:bg-surface disabled:opacity-40"
                    >
                      <div
                        className={`h-1.5 w-1.5 shrink-0 rounded-full ${r.spotify_id ? "bg-green-500" : "bg-neutral-600"}`}
                      />
                      <span className="text-[11px] text-neutral-300">
                        {r.name}
                      </span>
                      {r.genres.length > 0 && (
                        <span className="font-mono text-[10px] text-neutral-600">
                          {r.genres.slice(0, 2).join(", ")}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
              {searchQuery.length >= 2 && !isSearching && (
                <button
                  onClick={() => {
                    setShowCreateForm(true);
                    setNewArtistName(searchQuery);
                    setNewArtistImageUrl("");
                  }}
                  className="mt-1 font-mono text-[10px] text-acid/70 transition-colors hover:text-acid"
                >
                  + CREATE NEW ARTIST
                </button>
              )}
            </div>
          )}

          {/* Stage: dropdown of existing + custom option */}
          <div className="flex items-center gap-2">
            <label className="w-12 shrink-0 font-mono text-[10px] text-neutral-500">
              STAGE
            </label>
            {stage === "__custom__" ? (
              <div className="flex min-w-0 flex-1 items-center gap-1">
                <input
                  type="text"
                  value={customStage}
                  onChange={(e) => setCustomStage(e.target.value)}
                  placeholder="New stage name"
                  autoFocus
                  className="min-w-0 flex-1 rounded border border-neutral-700 bg-dark px-2 py-1 font-mono text-[11px] text-neutral-300 placeholder-neutral-600 outline-none focus:border-acid/50"
                />
                <button
                  type="button"
                  onClick={() => { setStage(""); setCustomStage(""); }}
                  className="shrink-0 font-mono text-[10px] text-neutral-500 hover:text-neutral-300"
                >
                  &times;
                </button>
              </div>
            ) : (
              <Select
                value={stage}
                onChange={setStage}
                size="sm"
                className="min-w-0 flex-1"
                options={[
                  { label: "No stage", value: "" },
                  ...existingStages.map((s) => ({ label: s, value: s })),
                  { label: "+ New stage...", value: "__custom__" },
                ]}
              />
            )}
          </div>
          <div className="flex items-center gap-2">
            <label className="w-12 shrink-0 font-mono text-[10px] text-neutral-500">
              TIME
            </label>
            <TimeInput value={setTime} onChange={setSetTime} />
          </div>
          <div className="flex items-center gap-2">
            <label className="w-12 shrink-0 font-mono text-[10px] text-neutral-500">
              END
            </label>
            <TimeInput value={setEndTime} onChange={setSetEndTime} />
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2 border-t border-neutral-800 p-5">
          <button
            onClick={onClose}
            className="rounded px-3 py-2.5 font-mono text-xs font-bold text-neutral-400 transition-colors cursor-pointer hover:text-white"
          >
            CANCEL
          </button>
          {(selectedArtist || (showCreateForm && newArtistName.trim())) && (
            <button
              onClick={handleAdd}
              disabled={isPending}
              className="flex-1 rounded bg-acid py-2.5 font-mono text-sm font-bold text-black transition-opacity cursor-pointer hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isPending ? "ADDING..." : showCreateForm ? "CREATE & ADD" : "ADD TO LINEUP"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
